import { useSegmentAnalytics } from "./useSegmentAnalytics";

export const useReferralLinksPageAnalytics = () => {
  const {
    trackButtonClickEvent,
    trackOptionClickEvent,
    trackInputChangeEvent,
  } = useSegmentAnalytics();

  const trackMarketingToolsMenuReferralLinksEvent = () =>
    trackButtonClickEvent({
      label: "Marketing tools menu - Referral links",
      location: "Referral links",
      extraProperties: {
        event_type: "Click",
        link_text: "Marketing tools menu - Referral links",
      },
    });

  const trackSearchInputEvent = (value: string) =>
    trackInputChangeEvent({
      label: "Referral links - Search Text box",
      location: "Referral links",
      extraProperties: {
        event_type: "Text input",
        link_text: "Search field",
        input_value: value,
      },
    });

  const trackFilterButtonEvent = () =>
    trackButtonClickEvent({
      label: "Referral links - Filter",
      location: "Referral links",
      extraProperties: {
        event_type: "Click",
        link_text: "Referral links - Filter",
      },
    });

  const trackCreateNewLinkButtonEvent = () =>
    trackButtonClickEvent({
      label: "Referral links - Create new link button",
      location: "Referral links",
      extraProperties: {
        event_type: "Click",
        link_text: "Referral links - Create new link button",
      },
    });

  const trackExportCsvButtonEvent = () =>
    trackButtonClickEvent({
      label: "Referral links - Export CSV button",
      location: "Referral links",
      extraProperties: {
        event_type: "Click",
        link_text: "Referral links - Export CSV button",
      },
    });

  const trackCopyIconEvent = (linkName: string) =>
    trackButtonClickEvent({
      label: "Referral links - Copy icon",
      location: "Referral links",
      extraProperties: {
        event_type: "Click",
        link_text: `${linkName} of the link copied`,
      },
    });

  const trackMoreMenuEvent = (linkName: string) =>
    trackButtonClickEvent({
      label: "Referral links - More menu",
      location: "Referral links",
      extraProperties: {
        event_type: "Click",
        link_text: `${linkName} for which More menu is clicked`,
      },
    });

  const trackMoreMenuCopyShortLinkEvent = (linkName: string) =>
    trackButtonClickEvent({
      label: "Referral links - More menu - Copy short link",
      location: "Referral links",
      extraProperties: {
        event_type: "Click",
        link_text: `${linkName} of the short link copied`,
      },
    });

  const trackMoreMenuGetQRCodeEvent = (linkName: string) =>
    trackButtonClickEvent({
      label: "Referral links - More menu - Get QR code",
      location: "Referral links",
      extraProperties: {
        event_type: "Click",
        link_text: `${linkName} of the QR code clicked`,
      },
    });

  const trackMoreMenuRenameEvent = (linkName: string) =>
    trackButtonClickEvent({
      label: "Referral links - More menu - Rename",
      location: "Referral links",
      extraProperties: {
        event_type: "Click",
        link_text: `${linkName} for which rename is clicked`,
      },
    });

  const trackMoreMenuPreviewLinkEvent = (linkName: string) =>
    trackButtonClickEvent({
      label: "Referral links - More menu - Preview link",
      location: "Referral links",
      extraProperties: {
        event_type: "Click",
        link_text: `${linkName} for which preview is clicked`,
      },
    });

  const trackViewPerformanceIconEvent = (linkName: string) =>
    trackButtonClickEvent({
      label: "Referral links - View performance icon",
      location: "Referral links",
      extraProperties: {
        event_type: "Click",
        link_text: `${linkName} for which view performance icon is clicked`,
      },
    });

  const trackMoreMenuViewPerformanceEvent = (linkName: string) =>
    trackButtonClickEvent({
      label: "Referral links - More menu - View performance",
      location: "Referral links",
      extraProperties: {
        event_type: "Click",
        link_text: `${linkName} for which view performance is clicked`,
      },
    });

  const trackFilterTargetPageSelectorEvent = (option: string) =>
    trackOptionClickEvent({
      label: "Referral links - Filter - Target page selector",
      location: "Referral links - Filter",
      extraProperties: {
        event_type: "Option clicked",
        link_text: option,
      },
    });

  const trackFilterLanguageSelectorEvent = (option: string) =>
    trackOptionClickEvent({
      label: "Referral links - Filter - Language selector",
      location: "Referral links - Filter",
      extraProperties: {
        event_type: "Option clicked",
        link_text: option,
      },
    });

  const trackFilterPlatformEvent = (value: string) =>
    trackButtonClickEvent({
      label: "Referral links - Filter - Platform",
      location: "Referral links - Filter",
      extraProperties: {
        event_type: "Click",
        link_text: value,
      },
    });

  const trackFilterRebateProgramSelectorEvent = (option: string) =>
    trackOptionClickEvent({
      label: "Referral links - Filter - Rebate program selector",
      location: "Referral links - Filter",
      extraProperties: {
        event_type: "Option clicked",
        link_text: option,
      },
    });

  const trackFilterCampaignSelectorEvent = (option: string) =>
    trackOptionClickEvent({
      label: "Referral links - Filter - Campaign",
      location: "Referral links - Filter",
      extraProperties: {
        event_type: "Option clicked",
        link_text: option,
      },
    });

  const trackFilterApplyEvent = (selection: {
    targetPages: string[];
    languages: string[];
    platforms: string[];
    rebatePrograms: string[];
    campaigns?: string[];
  }) =>
    trackButtonClickEvent({
      label: "Referral links - Filter - Apply",
      location: "Referral links - Filter",
      extraProperties: {
        event_type: "Click",
        link_text: [
          selection.targetPages,
          selection.languages,
          selection.platforms,
          selection.rebatePrograms,
          selection.campaigns ?? [],
        ]
          .filter((values) => values.length > 0)
          .map((values) => values.join(","))
          .join(" + "),
      },
    });

  const trackFilterClearEvent = () =>
    trackButtonClickEvent({
      label: "Referral links - Filter - Clear",
      location: "Referral links - Filter",
      extraProperties: {
        event_type: "Click",
        link_text: "Referral links - Filter - Clear",
      },
    });

  const trackRenameSaveEvent = (linkName: string) =>
    trackButtonClickEvent({
      label: "Referral links - Rename referral links - Save button",
      location: "Referral links - Rename",
      extraProperties: {
        event_type: "Click",
        link_text: linkName,
      },
    });

  const trackRenameCancelEvent = () =>
    trackButtonClickEvent({
      label: "Referral links - Rename referral links - Cancel button",
      location: "Referral links - Rename",
      extraProperties: {
        event_type: "Click",
        link_text: "Referral links - Rename referral links - Cancel button",
      },
    });

  const trackCreateLinkRebateProgramDropdownEvent = (option: string) =>
    trackOptionClickEvent({
      label: "Create Referral link - Rebate program dropdown",
      location: "Create referral link",
      extraProperties: {
        event_type: "Option clicked",
        link_text: option,
      },
    });

  const trackCreateLinkCampaignDropdownEvent = (option: string) =>
    trackOptionClickEvent({
      label: "Create Referral link - campaign selector",
      location: "Create referral link",
      extraProperties: {
        event_type: "Option clicked",
        link_text: option,
      },
    });

  const trackCreateLinkPlatformEvent = (value: string) =>
    trackButtonClickEvent({
      label: "Create Referral link - Platform",
      location: "Create referral link",
      extraProperties: {
        event_type: "Click",
        link_text: value,
      },
    });

  const trackCreateLinkTargetPageSelectorEvent = () =>
    trackButtonClickEvent({
      label: "Create Referral link - Target page selector",
      location: "Create referral link",
      extraProperties: {
        event_type: "Click",
        link_text: "Create Referral link - Target page selector",
      },
    });

  const trackCreateLinkTargetPageLanguageSelectorEvent = (option: string) =>
    trackOptionClickEvent({
      label: "Create Referral link - Target page - language selector",
      location: "Create referral link",
      extraProperties: {
        event_type: "Option clicked",
        link_text: option,
      },
    });

  const trackCreateLinkNameEvent = (value: string) =>
    trackInputChangeEvent({
      label: "Create Referral link - Name",
      location: "Create referral link",
      extraProperties: {
        event_type: "Text input",
        link_text: value,
        input_value: value,
      },
    });

  const trackCreateLinkCreateButtonEvent = (selection: {
    rebateProgram?: string;
    targetPage: string;
    language: string;
    name: string;
  }) =>
    trackButtonClickEvent({
      label: "Create Referral link - Create link button",
      location: "Create referral link",
      extraProperties: {
        event_type: "Click",
        link_text: [
          selection.rebateProgram ?? "-",
          selection.targetPage,
          selection.language,
          selection.name,
        ].join(" + "),
      },
    });

  const trackCreateLinkPreviewLinkButtonEvent = (linkName: string) =>
    trackButtonClickEvent({
      label: "Create Referral link - Preview link button",
      location: "Create referral link",
      extraProperties: {
        event_type: "Click",
        link_text: `${linkName} of the Preview link button`,
      },
    });

  return {
    trackMarketingToolsMenuReferralLinksEvent,
    trackSearchInputEvent,
    trackFilterButtonEvent,
    trackCreateNewLinkButtonEvent,
    trackExportCsvButtonEvent,
    trackCopyIconEvent,
    trackMoreMenuEvent,
    trackMoreMenuCopyShortLinkEvent,
    trackMoreMenuGetQRCodeEvent,
    trackMoreMenuRenameEvent,
    trackMoreMenuPreviewLinkEvent,
    trackViewPerformanceIconEvent,
    trackMoreMenuViewPerformanceEvent,
    trackFilterTargetPageSelectorEvent,
    trackFilterLanguageSelectorEvent,
    trackFilterPlatformEvent,
    trackFilterRebateProgramSelectorEvent,
    trackFilterCampaignSelectorEvent,
    trackFilterApplyEvent,
    trackFilterClearEvent,
    trackRenameSaveEvent,
    trackRenameCancelEvent,
    trackCreateLinkRebateProgramDropdownEvent,
    trackCreateLinkCampaignDropdownEvent,
    trackCreateLinkPlatformEvent,
    trackCreateLinkTargetPageSelectorEvent,
    trackCreateLinkTargetPageLanguageSelectorEvent,
    trackCreateLinkNameEvent,
    trackCreateLinkCreateButtonEvent,
    trackCreateLinkPreviewLinkButtonEvent,
  };
};
